import type { BlogTag } from '@/utils/blog-tags'

/** Превью ссылок блога: своя картинка, а не общая по сайту. */
export const BLOG_OG_IMAGE = '/og-blog.png'

export type BlogCategoryId = 'moex' | 'forex' | 'gold' | 'ruble' | 'eurusd' | 'blue-chips'

export interface BlogCategory {
  id: BlogCategoryId
  /** Ключ подписи в словаре: сами подписи переводятся вместе с интерфейсом */
  labelKey: string
}

export interface BlogArticle {
  id: number
  slug: string
  title: string
  /** Обложка из админки; пусто — рисуем свою по номеру материала */
  cover: string | null
  /**
   * Рубрика осталась в базе на случай отката и в отборе на сайте больше не
   * участвует — материал описывают теги.
   */
  category: BlogCategoryId
  /** Теги в порядке из админки: первый задаёт цвет обложки и бейджа */
  tags: BlogTag[]
  /** ISO-дата публикации, по ней строится сортировка «от новых к старым» */
  publishedAt: string
  /** Условная популярность: по ней отбираются материалы в ТОП-слайдер */
  views: number
  readingTime: number
  author: string
  /** Материал написал рефери, а не редакция: подпись под ним другая */
  authorReferrer?: boolean
  /** Фото рефери из его карточки; пусто — рисуем инициалы */
  authorPhoto?: string | null
}

export const BLOG_CATEGORIES: BlogCategory[] = [
  { id: 'moex', labelKey: 'blog.categories.moex' },
  { id: 'forex', labelKey: 'blog.categories.forex' },
  { id: 'gold', labelKey: 'blog.categories.gold' },
  { id: 'ruble', labelKey: 'blog.categories.ruble' },
  { id: 'eurusd', labelKey: 'blog.categories.eurusd' },
  { id: 'blue-chips', labelKey: 'blog.categories.blue-chips' }
]

/**
 * Отбор материалов идёт по тегу, а не по рубрике: у статьи их несколько, и
 * искать «всё про рубль» логичнее по метке, чем по единственному разделу.
 * `all` — весь список.
 */
export interface BlogFiltersValue {
  tag: string
}

export const createBlogFilters = (): BlogFiltersValue => ({
  tag: 'all'
})


/**
 * Разделы статьи. Пока это заголовки заглушки, но список общий для тела статьи
 * и для оглавления — иначе они разъезжаются при первой же правке текста.
 */
export const BLOG_ARTICLE_SECTIONS = [
  { id: 'market', title: 'Что происходило на рынке' },
  { id: 'money', title: 'Где были деньги' },
  { id: 'sectors', title: 'Как это выглядело по секторам' },
  { id: 'risks', title: 'Риски, которые мы держим в голове' },
  { id: 'takeaways', title: 'Что забрать в свою систему' }
]

/** Весь текст статей — временная заглушка, реальные материалы приедут из админки. */
export const BLOG_PLACEHOLDER_EXCERPT = 'Текст-заглушка. Здесь будет краткий анонс материала на две-три строки: о чём статья, к какому выводу пришла аналитическая команда и что из этого стоит забрать в свою торговую систему.'

export const BLOG_ARTICLES: BlogArticle[] = [
  { id: 1, slug: 'article-1', title: 'Итоги недели на Мосбирже: что двигало индекс', category: 'moex', publishedAt: '2026-07-30', views: 18420, readingTime: 6, author: 'Аналитический отдел АДФ' },
  { id: 2, slug: 'article-2', title: 'Золото у исторических максимумов: разбираем причины', category: 'gold', publishedAt: '2026-07-29', views: 22150, readingTime: 8, author: 'Аналитический отдел АДФ' },
  { id: 3, slug: 'article-3', title: 'EURUSD: сценарии на ближайший месяц', category: 'eurusd', publishedAt: '2026-07-28', views: 15310, readingTime: 5, author: 'Аналитический отдел АДФ' },
  { id: 4, slug: 'article-4', title: 'Рубль после заседания ЦБ: чего ждать трейдеру', category: 'ruble', publishedAt: '2026-07-26', views: 26840, readingTime: 7, author: 'Аналитический отдел АДФ' },
  { id: 5, slug: 'article-5', title: 'Голубые фишки: кто вытянул портфель в июле', category: 'blue-chips', publishedAt: '2026-07-24', views: 12470, readingTime: 6, author: 'Аналитический отдел АДФ' },
  { id: 6, slug: 'article-6', title: 'FOREX для новичка: как не слить депозит на старте', category: 'forex', publishedAt: '2026-07-22', views: 31200, readingTime: 9, author: 'Наставники АДФ' },
  { id: 7, slug: 'article-7', title: 'Дивидендный сезон на МОЕХ: календарь и расчёты', category: 'moex', publishedAt: '2026-07-20', views: 9840, readingTime: 5, author: 'Аналитический отдел АДФ' },
  { id: 8, slug: 'article-8', title: 'Хеджирование валютного риска: три рабочие схемы', category: 'forex', publishedAt: '2026-07-18', views: 8620, readingTime: 7, author: 'Аналитический отдел АДФ' },
  { id: 9, slug: 'article-9', title: 'Почему золото перестало быть «тихой гаванью»', category: 'gold', publishedAt: '2026-07-16', views: 14090, readingTime: 6, author: 'Аналитический отдел АДФ' },
  { id: 10, slug: 'article-10', title: 'Рубль и нефть: связь, которой уже нет', category: 'ruble', publishedAt: '2026-07-14', views: 11730, readingTime: 8, author: 'Аналитический отдел АДФ' },
  { id: 11, slug: 'article-11', title: 'EURUSD: как торговать выход статистики', category: 'eurusd', publishedAt: '2026-07-12', views: 7460, readingTime: 4, author: 'Наставники АДФ' },
  { id: 12, slug: 'article-12', title: 'Разбор сделки: вход по объёмам в голубых фишках', category: 'blue-chips', publishedAt: '2026-07-10', views: 10250, readingTime: 10, author: 'Наставники АДФ' },
  { id: 13, slug: 'article-13', title: 'Ликвидность на утренней сессии: где прячется риск', category: 'moex', publishedAt: '2026-07-08', views: 6380, readingTime: 5, author: 'Аналитический отдел АДФ' },
  { id: 14, slug: 'article-14', title: 'Кэрри-трейд в 2026 году: считаем на пальцах', category: 'forex', publishedAt: '2026-07-05', views: 9120, readingTime: 7, author: 'Аналитический отдел АДФ' },
  { id: 15, slug: 'article-15', title: 'Физическое золото против фьючерса: что выбрать', category: 'gold', publishedAt: '2026-07-03', views: 13640, readingTime: 6, author: 'Аналитический отдел АДФ' },
  { id: 16, slug: 'article-16', title: 'Курс рубля: разбираем сезонность за десять лет', category: 'ruble', publishedAt: '2026-07-01', views: 5910, readingTime: 9, author: 'Аналитический отдел АДФ' },
  { id: 17, slug: 'article-17', title: 'Психология трейдера: как пережить серию убытков', category: 'blue-chips', publishedAt: '2026-06-28', views: 20380, readingTime: 8, author: 'Наставники АДФ' },
  { id: 18, slug: 'article-18', title: 'Спред и проскальзывание: скрытая стоимость сделки', category: 'forex', publishedAt: '2026-06-25', views: 7240, readingTime: 5, author: 'Аналитический отдел АДФ' },
  { id: 19, slug: 'article-19', title: 'Индекс МОЕХ: техническая картина на квартал', category: 'moex', publishedAt: '2026-06-22', views: 8890, readingTime: 6, author: 'Аналитический отдел АДФ' },
  { id: 20, slug: 'article-20', title: 'EURUSD и решения ЕЦБ: чек-лист перед заседанием', category: 'eurusd', publishedAt: '2026-06-19', views: 6720, readingTime: 4, author: 'Аналитический отдел АДФ' },
  { id: 21, slug: 'article-21', title: 'Как мы отбираем трейдеров в АДФ Капитал', category: 'blue-chips', publishedAt: '2026-06-16', views: 17560, readingTime: 7, author: 'Команда АДФ' },
  { id: 22, slug: 'article-22', title: 'Золото в портфеле: какая доля действительно нужна', category: 'gold', publishedAt: '2026-06-12', views: 9350, readingTime: 6, author: 'Аналитический отдел АДФ' },
  { id: 23, slug: 'article-23', title: 'Рубль, ставка и вклады: где деньги работают лучше', category: 'ruble', publishedAt: '2026-06-09', views: 12980, readingTime: 8, author: 'Аналитический отдел АДФ' },
  { id: 24, slug: 'article-24', title: 'Риск-менеджмент: правило одного процента на практике', category: 'forex', publishedAt: '2026-06-05', views: 24730, readingTime: 9, author: 'Наставники АДФ' },
  { id: 25, slug: 'article-25', title: 'Отчётности эмитентов: что читать, а что пропустить', category: 'moex', publishedAt: '2026-06-02', views: 5480, readingTime: 7, author: 'Аналитический отдел АДФ' },
  { id: 26, slug: 'article-26', title: 'Голубые фишки против второго эшелона: сравнение', category: 'blue-chips', publishedAt: '2026-05-29', views: 8130, readingTime: 6, author: 'Аналитический отдел АДФ' },
  { id: 27, slug: 'article-27', title: 'Торговый план на день: шаблон нашей команды', category: 'eurusd', publishedAt: '2026-05-26', views: 15870, readingTime: 5, author: 'Наставники АДФ' }
]
